// React imports
import React, { useMemo, useEffect, useState } from "react";

// UI Elements
import Button from "../../../../ui-elements/Button/Button";
import DisplayBox from "../../../../ui-elements/DisplayBox/DisplayBox";
import Link from "../../../../ui-elements/Link/Link";
import Loading from "../../../../ui-elements/Loading/Loading";
import Table from "../../../../ui-elements/Table/Table";
import Tooltip from "../../../../ui-elements/Tooltip/Tooltip";

// Icons
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/pro-solid-svg-icons";

// PEP Components
import ForeignCompanyPepModal from "../../../PEP/ForeignCompanyPepModal";
import { formatCategories } from "../../../PEP/PepPage";

// Types
import { ForeignCompanyPep, PepCompanyResult } from "../../../../types/ForeignCompanyPepTypes";
import AlthjodlegirValistarForm from "./AlthjodlegirValistarForm";
import { CountryList } from "../../../../assets/staticData/CountryList";
import { ForeignCompanyPepReport } from "../../../../types/CompanyAnswersTypes";

export const SanctionsInfoTooltip: React.FC = () => (
    <Tooltip
        tooltipClassName="viewCompanyAnswers-tooltip"
        noUnderline
        tooltip={<div className="tooltip-text">
            <p><strong>Alþjóðlegir válistar</strong></p>
            Tilkynningarskyldir aðilar geta flett upp einstaklingum á móti alþjóðlegum þvingunarlistum, PEP og neikvæðri umfjöllun (e. adverse media). Leitað er eftir nafni, fæðingardegi og þjóðerni.
            Sbr. lög nr. 140/2018 um peningaþvætti og lög nr. 68/2023 um frystingu fjármuna og um framkvæmd alþjóðlegra þvingunaraðgerða, ber tilkynningarskyldum aðilum að kanna hvort viðskiptavinir séu á slíkum alþjóðlegum válistum.
            Listarnir sem kerfið keyrir á móti uppfylla kröfur AML5, FATF, UNCAC, JMLSG, UK/FCA og Wolfsberg Group um peningaþvætti og sækir upplýsingar m.a. í
            United Nations Sanctions (UN)<br />
            US Consolidated Sanctions <br />
            OFAC – Specially Designated Nationals (SDN) <br />
            EU Financial Sanctions<br />
            UK Financial Sanctions (HMT)<br />
            Australian Sanctions<br />
            Switzerland Sanction List – SECO<br />
            INTERPOL Wanted List<br />
            Consolidated Canadian Autonomous Sanctions List<br />
            Office of the Superintendent of Financial Institutions (Canada)<br />
            Bureau of Industry and Security (US)<br />
            Department of State, AECA Debarred List (US)<br />
            Department of State, Nonproliferation Sanctions (US)<br />
        </div>}
        text={<FontAwesomeIcon className="fa-icon" icon={faInfoCircle} />}
    />
);

interface IProps {
    initialName: string;
    initialCountryCode: string;
    riskscoreId: string | null;
    searchHistory?: ForeignCompanyPepReport[] | null;
}

const AlthjodlegirValistar: React.FC<IProps> = ({ initialName, initialCountryCode, riskscoreId, searchHistory }) => {
    // State for current search results (when user performs a new search)
    const [searchResults, setSearchResults] = useState<ForeignCompanyPep | null | undefined>(undefined);

    // State for initial lookup data passed from parent component
    const [initialLookUp, setInitialLookUp] = useState<PepCompanyResult[] | undefined>(
        searchHistory?.[searchHistory.length - 1]?.mappedReport?.match ?? undefined
    );

    // Track whether the user has performed their own search
    // This prevents the useEffect from overwriting intentional state clears
    const [hasPerformedSearch, setHasPerformedSearch] = useState(false);

    // Modal states
    const [companyShowModal, setCompanyShowModal] = useState(false);
    const [companyModalData, setCompanyModalData] = useState<PepCompanyResult | undefined>(undefined);

    // Sync lastLookUp prop with initialLookUp state when the component receives data from parent
    // Only updates if user hasn't performed their own search yet
    useEffect(() => {
        // Only update from parent prop if:
        // 1. lastLookUp has data
        // 2. User hasn't performed their own search yet
        const lastLookUp = searchHistory?.[searchHistory.length - 1]?.mappedReport?.match;
        if (lastLookUp && !hasPerformedSearch) {
            setInitialLookUp(lastLookUp);
        }
    }, [searchHistory, hasPerformedSearch]);

    /**
     * Renders the search results section with a table displaying company matches.
     * Handles three states:
     * 1. undefined - No search performed yet (returns empty fragment)
     * 2. null - Search in progress (shows loading indicator)
     * 3. SearchResults - Display results in a formatted table with company information
     * 
     * @returns React element representing the results section or empty fragment
     */
    const displayResultSection = useMemo((): React.ReactElement => {
        // Wrapper component that provides consistent styling and header for the results section
        const ContentWrapper = ({ children, name }: { children: React.ReactNode; name: string }) => {
            return (
                <div className="section_results">
                    <h5>Niðurstöður fyrir {name}:</h5>
                    {children}
                </div>
            );
        };

        // Return empty fragment if no search has been initiated yet and no initial data exists
        if (searchResults === undefined && initialLookUp === undefined) {
            return <></>;
        }

        // Show loading state while search is in progress
        if (searchResults === null && initialLookUp === undefined) {
            return (
                <ContentWrapper name={initialName}>
                    <Loading />
                </ContentWrapper>
            );
        }

        /**
         * Handles the click event when user wants to view detailed company information.
         * Opens a modal with the selected company's full details.
         * 
         * @param company - The company data object to display in the modal
         */
        const handleViewCompanyDetails = (company: PepCompanyResult): void => {
            setCompanyModalData(company);
            setCompanyShowModal(true);
        };
        // Get the last lookup's company name without removing it from the array
        const lastLookUpTitle = searchHistory?.[searchHistory.length - 1]?.mappedReport?.request?.companyName;


        // Render the results table with company data wrapped in the ContentWrapper
        // Priority: initialLookUp (from parent) > searchResults (from user search)
        return (
            <ContentWrapper name={searchResults?.request.companyName ?? lastLookUpTitle ?? ''} >
                <Table
                    tableSize="lg"
                    data={initialLookUp ?? searchResults?.match ?? []}
                    showEmptyTableText
                    columns={[
                        {
                            title: "Nafn",
                            renderCell: ({ nameDetails }) => nameDetails?.[0]?.companyName ?? "",
                            textAlign: "left",
                        },
                        {
                            title: "Land",
                            renderCell: ({ locations }) => CountryList.find(country => country.value === locations?.[0]?.country)?.label || "",
                            textAlign: "left",
                        },
                        {
                            title: "Flokkur/ar",
                            renderCell: ({ categories }) => formatCategories(categories),
                            textAlign: "left",
                        },
                        {
                            title: "Lýsing",
                            renderCell: ({ generalInfo }) => generalInfo?.businessDescription ?? "",
                            textAlign: "left",
                        },
                        {
                            title: "Ítarefni",
                            renderCell: (item) => (
                                <Button
                                    onClick={() => handleViewCompanyDetails(item)}
                                    buttonType="secondary"
                                    size="sm"
                                >
                                    Skoða
                                </Button>
                            ),
                        },
                    ]}
                />
            </ContentWrapper>
        );
    }, [searchResults, initialLookUp]);

    return (
        <DisplayBox
            id="althjodlegir-valistar"
            title="Alþjóðlegir válistar">
            <div className="content">
                <div className="section_form">
                    <p className="paragraph-small">
                        Hægt er að kanna stöðu lögaðila gagnvart alþjóðlegum þvingunarlistum og öðrum válistum.
                        Athugið að hver uppfletting er samkvæmt{" "}
                        <Link linkSize="16" url="/Verdskra#Gagnaleit">
                            verðskrá keldunnar
                        </Link>.
                    </p>
                    <div className="section_form--inputs">
                        <AlthjodlegirValistarForm
                            riskscoreId={riskscoreId}
                            initialValues={{ name: initialName, country_code: initialCountryCode }}
                            changeSearchResults={(e) => {
                                // Mark that user has performed a search
                                // This prevents the useEffect from overwriting these intentional changes
                                setHasPerformedSearch(true);

                                // Clear the initial lookup data to show new search results
                                setInitialLookUp(undefined);

                                // Set the new search results from the form
                                setSearchResults(e);
                            }}
                            isDisabled={false}
                        />
                    </div>
                </div>
                {displayResultSection}
                {/* <h4>Eldri niðurstöður</h4>
                <Table data={lastLookUp} columns={[]} /> */}
            </div>

            <ForeignCompanyPepModal
                show={companyShowModal}
                close={() => setCompanyShowModal(false)}
                data={companyModalData}
            />
        </DisplayBox>
    );
};

export default AlthjodlegirValistar;